import { useCallback, useState } from "react";
import { APPS, type AppEntry } from "./registry/apps";
import { computeProgress } from "./lib/progress";
import { usePrefersReducedMotion } from "./lib/motion";
import { useWallOrder } from "./lib/use-wall-order";
import { wrapIndex } from "./lib/wall-order";
import { Mosaic } from "./components/Mosaic";
import { ProgressHUD } from "./components/ProgressHUD";
import { InventoryPanel } from "./components/InventoryPanel";
import { AppModal } from "./components/AppModal";
import { Grain } from "./components/Grain";

export default function App() {
  const reducedMotion = usePrefersReducedMotion();
  const progress = computeProgress(APPS);
  const { order, ordered, selectOrder, reshuffle } = useWallOrder(APPS);
  const [inventoryOpen, setInventoryOpen] = useState(false);
  const [selected, setSelected] = useState<AppEntry | null>(null);
  const openInventory = useCallback(() => {
    setSelected(null);
    setInventoryOpen(true);
  }, []);
  const closeInventory = useCallback(() => setInventoryOpen(false), []);
  const toggleInventory = useCallback(
    () =>
      setInventoryOpen((open) => {
        if (!open) setSelected(null);
        return !open;
      }),
    [],
  );
  const openApp = useCallback((app: AppEntry) => {
    setInventoryOpen(false);
    setSelected(app);
  }, []);
  const closeApp = useCallback(() => setSelected(null), []);
  const stepApp = useCallback(
    (delta: number) =>
      setSelected((app) => {
        if (!app || ordered.length === 0) return app;
        const i = ordered.findIndex((a) => a.slug === app.slug);
        return ordered[wrapIndex(i + delta, ordered.length)] ?? app;
      }),
    [ordered],
  );
  const prevApp = useCallback(() => stepApp(-1), [stepApp]);
  const nextApp = useCallback(() => stepApp(1), [stepApp]);
  const wallBlocked = inventoryOpen || selected !== null;

  return (
    <div className="page">
      <a
        className="skip"
        href="#inventory"
        onClick={(event) => {
          event.preventDefault();
          openInventory();
        }}
      >
        Skip to live apps
      </a>

      <p className="sr-only">
        The hub is a fullscreen mosaic wall of live apps. Drag or scroll to pan. Pinch or hold
        Control and scroll to zoom. Arrow keys pan, plus and minus zoom, zero resets the view.
        The order control chooses featured, ship order, or random. Click a tile for a summary,
        then open the path URL. Press slash or APPS to open the app list.
      </p>

      <Grain reducedMotion={reducedMotion} />

      <section className="hero" aria-hidden={wallBlocked ? true : undefined}>
        <Mosaic
          apps={ordered}
          progressCount={progress.count}
          progressRemaining={progress.remaining}
          reducedMotion={reducedMotion}
          interactive={!wallBlocked}
          onOpenApp={openApp}
        />
        <div className="hero-veil" aria-hidden="true" />
        <ProgressHUD
          progress={progress}
          appsOpen={inventoryOpen}
          onToggleApps={toggleInventory}
          order={order}
          onOrder={selectOrder}
          onReshuffle={reshuffle}
        />

        <div className="lockup">
          <h1>
            <span className="line-over">OVER</span>
            <span className="line-nine">9000</span>
          </h1>
        </div>

        <p className="spine" aria-hidden="true">
          31 DEC 2026 · HIT 9000 · SHIP DAILY
        </p>

        <p className={`surf-hint ${reducedMotion ? "is-still" : ""}`} aria-hidden="true">
          <span className="hint-desktop">
            Drag / scroll to surf · pinch or ctrl+wheel zoom · 0 reset · / apps
          </span>
          <span className="hint-mobile">Drag to surf · pinch zoom · APPS for the list</span>
        </p>
      </section>

      <InventoryPanel
        apps={APPS}
        open={inventoryOpen}
        hotkeys={selected === null}
        onOpen={openInventory}
        onClose={closeInventory}
      />

      {selected ? <AppModal app={selected} onClose={closeApp} onPrev={prevApp} onNext={nextApp} /> : null}
    </div>
  );
}
